import { useEffect } from 'react';
import { showSnackbar } from './Snackbar';

let wakeLock = null;
let shouldStayAwake = false;
let retryTimer = null;
let failureCount = 0;
let warningShown = false;

const canUseWakeLock = () =>
  typeof navigator !== 'undefined' && 'wakeLock' in navigator;

const clearRetry = () => {
  if (retryTimer) {
    clearTimeout(retryTimer);
    retryTimer = null;
  }
};

const showManualScreenWarning = () => {
  if (warningShown) return;
  warningShown = true;
  showSnackbar(
    'Keep your screen awake manually during the queue and call on this browser.',
    'warning',
  );
};

const scheduleRetry = () => {
  clearRetry();
  if (!shouldStayAwake || document.visibilityState !== 'visible') return;
  retryTimer = setTimeout(() => {
    requestScreenWakeLock();
  }, 1200);
};

export const requestScreenWakeLock = async () => {
  if (!shouldStayAwake || document.visibilityState !== 'visible') return false;
  if (!canUseWakeLock()) {
    showManualScreenWarning();
    return false;
  }
  if (wakeLock && !wakeLock.released) return true;

  try {
    wakeLock = await navigator.wakeLock.request('screen');
    failureCount = 0;
    wakeLock.addEventListener('release', () => {
      wakeLock = null;
      scheduleRetry();
    }, { once: true });
    return true;
  } catch (_) {
    failureCount += 1;
    if (failureCount >= 2) showManualScreenWarning();
    scheduleRetry();
    return false;
  }
};

export const enableScreenWakeLock = async () => {
  shouldStayAwake = true;
  warningShown = false;
  return requestScreenWakeLock();
};

export const disableScreenWakeLock = async () => {
  shouldStayAwake = false;
  clearRetry();
  const currentLock = wakeLock;
  wakeLock = null;
  if (!currentLock || currentLock.released) return;
  try {
    await currentLock.release();
  } catch (_) {
    // The browser may already have released the lock.
  }
};

const useWakeLock = () => {
  useEffect(() => {
    const resumeWakeLock = () => {
      if (document.visibilityState === 'visible' && shouldStayAwake) {
        requestScreenWakeLock();
      }
    };
    const wakeEvents = ['pointerdown', 'touchstart', 'click', 'keydown'];
    const heartbeat = setInterval(resumeWakeLock, 15000);

    document.addEventListener('visibilitychange', resumeWakeLock);
    window.addEventListener('focus', resumeWakeLock);
    window.addEventListener('pageshow', resumeWakeLock);
    wakeEvents.forEach((eventName) => {
      window.addEventListener(eventName, resumeWakeLock, { passive: true });
    });

    return () => {
      clearInterval(heartbeat);
      clearRetry();
      document.removeEventListener('visibilitychange', resumeWakeLock);
      window.removeEventListener('focus', resumeWakeLock);
      window.removeEventListener('pageshow', resumeWakeLock);
      wakeEvents.forEach((eventName) => {
        window.removeEventListener(eventName, resumeWakeLock);
      });
    };
  }, []);
};

export default useWakeLock;
